import React, { useState } from "react";
import { X, Search, Pencil, Trash2, CheckCircle2, Circle, Check} from "lucide-react";

/* -------------------- Button -------------------- */
function Button({ children, className = "", ...props }) {
  return (
    <button className={`btn ${className}`} {...props}>
      {children}
    </button>
  );
}

/* -------------------- Input -------------------- */
function Input({ className = "", ...props }) {
  return <input className={`input ${className}`} {...props} />;
}

/* -------------------- Task Manager -------------------- */
const initialTasks = [
  { id: "1", title: "Learn React", status: "completed", priority: "high" },
  { id: "2", title: "Build UI", status: "in-progress", priority: "medium" },
  { id: "3", title: "Write CSS", status: "pending", priority: "low" },
];

export function TaskManager({ onClose }) {
  const [tasks, setTasks] = useState(initialTasks);
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddTask, setShowAddTask] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newTaskPriority, setNewTaskPriority] = useState("medium");
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editTaskTitle, setEditTaskTitle] = useState("");

  const filteredTasks = tasks.filter((task) => {
    const matchesSearch = task.title.toLowerCase().includes(searchQuery.toLowerCase());
    if (filter === "all") return matchesSearch;
    if (filter === "active") return matchesSearch && task.status !== "completed";
    if (filter === "completed") return matchesSearch && task.status === "completed";
    return matchesSearch;
  });

  const activeTasksCount = tasks.filter((task) => task.status !== "completed").length;

  const handleAddTask = () => {
    if (newTaskTitle.trim()) {
      const newTask = {
        id: Date.now().toString(),
        title: newTaskTitle,
        status: "pending",
        priority: newTaskPriority,
      };
      setTasks([...tasks, newTask]);
      setNewTaskTitle("");
      setNewTaskPriority("medium");
      setShowAddTask(false);
    }
  };

  const handleDeleteTask = (taskId) => {
    setTasks(tasks.filter((task) => task.id !== taskId));
  };

  const handleClearCompleted = () => {
    setTasks(tasks.filter((task) => task.status !== "completed"));
  };

  const handleEditTask = (taskId) => {
    const task = tasks.find((t) => t.id === taskId);
    if (task) {
      setEditingTaskId(taskId);
      setEditTaskTitle(task.title);
    }
  };

  const handleSaveEdit = (taskId) => {
    if (editTaskTitle.trim()) {
      setTasks(
        tasks.map((task) =>
          task.id === taskId ? { ...task, title: editTaskTitle } : task
        )
      );
    }
    setEditingTaskId(null);
    setEditTaskTitle("");
  };

  const handleCancelEdit = () => {
    setEditingTaskId(null);
    setEditTaskTitle("");
  };

  return (
    <div className="task-manager">
      {/* Header */}
      <div className="header">
        <div>
          <h2>Task Manager</h2>
          <p>Organize, prioritize, and track your work seamlessly.</p>
        </div>
        <button className="close-btn" onClick={onClose}><X size={20} /></button>
      </div>

      {/* Search Bar */}
      <div className="search-bar">
        <Search className="search-icon" size={18} />
        <Input
          type="text"
          placeholder="Search task Here.."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Filters */}
      <div className="filters">
        <div>
          <Button onClick={() => setFilter("all")} className={filter === "all" ? "active" : ""}>All</Button>
          <Button onClick={() => setFilter("active")} className={filter === "active" ? "active" : ""}>Active</Button>
          <Button onClick={() => setFilter("completed")} className={filter === "completed" ? "active" : ""}>Completed</Button>
        </div>
        <Button onClick={() => setShowAddTask(!showAddTask)} className="add-task-btn">Add Task</Button>
      </div>

      {/* Add Task */}
      {showAddTask && (
        <div className="add-task">
          <Input
            type="text"
            placeholder="What needs to be done?"
            value={newTaskTitle}
            onChange={(e) => setNewTaskTitle(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleAddTask()}
          />
            <div className="add-task-actionbtn">
                <select value={newTaskPriority} onChange={(e) => setNewTaskPriority(e.target.value)}>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                </select>
                <div>
                    <Button onClick={() => setShowAddTask(false)}>Cancel</Button>
                    <Button onClick={handleAddTask} className="primary">Add</Button>
                </div>
            </div>
        </div>
      )}

      {/* Task List */}
      <div className="task-list">
        {filteredTasks.map((task) => (
          <div key={task.id} className="task">
            {editingTaskId === task.id ? (
              <div className="edit-task">
                <Input
                  type="text"
                  value={editTaskTitle}
                  onChange={(e) => setEditTaskTitle(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSaveEdit(task.id)}
                />
                <button onClick={() => handleSaveEdit(task.id)} className="action-btn"><Check /></button>
                <button onClick={handleCancelEdit} className="action-btn"><X /></button>
              </div>
            ) : (
              <>
                <span className="task-title">{task.title} <span className={`badge ${task.priority}`}>{task.priority}</span></span>
                
                <div className="actions">
                  <button onClick={() => handleEditTask(task.id)} className="action-btn"><Pencil size={16} /></button>
                  <button onClick={() => handleDeleteTask(task.id)} className="action-btn"><Trash2 size={16} /></button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="footer">
        <span>{activeTasksCount} items left</span>
        <button className="clear-btn" onClick={handleClearCompleted}>Clear Completed</button>
      </div>
    </div>
  );
}
